import { Modal, Popover } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Virtuoso } from "react-virtuoso";
import styled from "styled-components";

import type { OrgBriefDto } from "@superblocksteam/shared";

import { PrimaryButton } from "components/ui/Button";
import { ConfirmModalClass } from "components/ui/Modal";
import { Spinner } from "components/ui/Spinner";
import { SearchInput } from "core/components/SearchInput";
import { WithTooltip } from "core/components/WithTooltip";
import { editorTopNavChrome } from "core/editor/constants/editorTopNavChrome";
import { getSuperblocksHQOrgName } from "env";
import { scrollbarLightCss } from "legacy/constants/DefaultTheme";
import type { User } from "legacy/constants/userConstants";
import { selectOnlyOrganization } from "store/slices/organizations";
import { callServer, HttpMethod } from "store/utils/client";
import { colors } from "styles/colors";
import { styleAsClass } from "styles/styleAsClass";
import { sendErrorUINotification } from "utils/notification";

type Org = {
  id: string;
  name: string;
};

const OrgListStyles = styleAsClass`
  width: 280px;
  ${scrollbarLightCss}
`;

const ListItem = styleAsClass`
  padding: 8px 12px;
  font-size: 12px;
  line-height: 16px;
  height: 32px;
  border-radius: 4px;
  &:hover {
    cursor: pointer;
    background-color: ${colors.GREY_100};
  }
`;

const ButtonWrapper = styled.div`
  margin-left: 10px;
`;

const SwitchOrgButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${editorTopNavChrome.controlHeightPx}px;
  padding: 0 10px;
  border: 1px solid ${editorTopNavChrome.segmentedBorder};
  border-radius: ${editorTopNavChrome.tabRadiusPx}px;
  background: ${editorTopNavChrome.barBackground};
  color: ${editorTopNavChrome.tabActive};
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  margin-right: 10px;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${editorTopNavChrome.iconButtonHoverBg};
    color: ${editorTopNavChrome.iconHover};
  }
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const getAllOrgs = () => {
  return callServer<OrgBriefDto[]>({
    method: HttpMethod.Get,
    url: `v1/management/orgs`,
  });
};

const switchUserOrg = (organizationId: string) => {
  return callServer<User>({
    method: HttpMethod.Post,
    url: `v1/management/switch-org/organization/${organizationId}`,
  });
};

const OrgSwitcher = () => {
  const [allOrgs, setAllOrgs] = useState<OrgBriefDto[]>([]);
  const [isAllOrgsLoading, setIsAllOrgsLoading] = useState(false);
  const organization = useSelector(selectOnlyOrganization);

  const fetchAllOrgs = useCallback(async () => {
    try {
      setIsAllOrgsLoading(true);
      const orgs = await getAllOrgs();
      setAllOrgs(orgs);
    } catch (error: any) {
      sendErrorUINotification({
        message: `Failed to fetch all orgs. ${error?.message}`,
      });
    }
    setIsAllOrgsLoading(false);
  }, []);

  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const onSearch = useCallback((value: string) => {
    setSearchKeyword(value);
  }, []);

  const filteredOrgs: Org[] = useMemo(() => {
    return allOrgs?.filter((org) =>
      org.name.toLocaleLowerCase().includes(searchKeyword.toLocaleLowerCase()),
    );
  }, [allOrgs, searchKeyword]);

  const superblocksOrgName = getSuperblocksHQOrgName();
  const superblocksOrg = useMemo(
    () =>
      superblocksOrgName
        ? allOrgs.find((org) => org.name === superblocksOrgName)
        : undefined,
    [allOrgs, superblocksOrgName],
  );

  const onSwitch = useCallback(async (org: Org) => {
    try {
      await switchUserOrg(org.id);
      window.location.reload();
    } catch (error: any) {
      sendErrorUINotification({
        message: `Failed to switch org to ${org.name}. ${error?.message}`,
      });
    }
  }, []);

  const onReset = useCallback(() => {
    if (superblocksOrg?.id) {
      onSwitch(superblocksOrg);
    }
  }, [onSwitch, superblocksOrg]);

  const renderOrgList = useCallback(
    (index: number, org: Org) => {
      return (
        <div
          className={ListItem}
          onClick={() => {
            Modal.confirm({
              title: `Are you sure you want to switch to ${org.name}?`,
              icon: <></>,
              okText: "Confirm",
              okType: "primary",
              onOk: () => {
                onSwitch(org);
              },
              zIndex: 1040,
              className: `${ConfirmModalClass}`,
            });
          }}
        >
          {org.name}
        </div>
      );
    },
    [onSwitch],
  );

  const switchOrgContent = (
    <div>
      <StyledRow style={{ fontSize: "12px" }}>
        <div>
          <b>Current Org</b>: {organization.name}
        </div>
        <ButtonWrapper>
          <WithTooltip content={`Go back to ${superblocksOrgName}`}>
            <PrimaryButton onClick={onReset}>Reset</PrimaryButton>
          </WithTooltip>
        </ButtonWrapper>
      </StyledRow>
      <SearchInput
        className="mx-auto h-8 w-full"
        inputClassName="!text-xs !leading-4"
        data-test="orgs-search"
        onSearch={onSearch}
        value={""}
        placeholder={"Search Orgs"}
      />
      <Spinner spinning={isAllOrgsLoading}>
        <Virtuoso
          style={{ height: "600px" }}
          totalCount={filteredOrgs.length}
          itemContent={renderOrgList}
          data={filteredOrgs}
          className={OrgListStyles}
        />
      </Spinner>
    </div>
  );

  return (
    <Popover
      trigger="click"
      content={switchOrgContent}
      destroyTooltipOnHide
      onOpenChange={(open) => {
        if (!open) {
          setSearchKeyword("");
        }
      }}
    >
      <SwitchOrgButton type="button" onClick={() => fetchAllOrgs()}>
        Switch Org
      </SwitchOrgButton>
    </Popover>
  );
};

export default OrgSwitcher;
